// export let localLoggingEnabled = process?.env?.NODE_ENV !== "production";
export let localLoggingEnabled = false; // use this line instead of above line to turn off extra logging when running locally
let deltaLoggingEnabled = false;

//
try {
    if (!window.localStorage.localLoggingEnabled) {
        window.localStorage.localLoggingEnabled = "false";
    }
    // create an entry for dev to modify in the inspector application tab
    if (!window.localStorage.deltaLoggingEnabled) {
        window.localStorage.deltaLoggingEnabled = "false";
    }
    localLoggingEnabled = window.localStorage.localLoggingEnabled === "true";
    deltaLoggingEnabled = window.localStorage.deltaLoggingEnabled === "true";
} catch {
    console.warn("localStorage is not enabled.");
}

export class Logger {
    private logs: string[] = [];
    private readonly maximumLogLength = 1000;
    private tmLast: number = new Date().getTime();
    enabled = false;

    constructor() {
        Log = this;
        this.enabled = localLoggingEnabled;
    }
    enable(value: boolean) {
        this.enabled = value;
    }

    table(message: string, object?: any): void {
        this.log("INFO", message, object ? JSON.stringify(object) : "");

        if (this.enabled) {
            const sanitizedMessage = Logger.sanitizeForLogging(message);
            const sanitizedObject = object !== undefined ? Logger.sanitizeForLogging(object) : object;
            console.log(`INFO: ${this.addTimeStamp(sanitizedMessage)}`);
            console.table(sanitizedObject);
        }
    }

    info(message: string, object?: any): void {
        this.log("INFO", message, object);
        if (this.enabled) {
            const sanitizedMessage = Logger.sanitizeForLogging(message);
            const sanitizedObject = object !== undefined ? Logger.sanitizeForLogging(object) : object;
            console.info(`INFO: ${this.addTimeStamp(sanitizedMessage, sanitizedObject)}`);
        }
    }

    warning(message: string, object?: any): void {
        this.log("WARN", message, object);
        if (this.enabled) {
            const sanitizedMessage = Logger.sanitizeForLogging(message);
            const sanitizedObject = object !== undefined ? Logger.sanitizeForLogging(object) : object;
            console.warn(`WARN: ${this.addTimeStamp(sanitizedMessage, sanitizedObject)}`);
        }
    }

    error(message: string, object?: any): void {
        this.log("ERROR", message, object);
        if (this.enabled) {
            const sanitizedMessage = Logger.sanitizeForLogging(message);
            const sanitizedObject = object !== undefined ? Logger.sanitizeForLogging(object) : object;
            console.error(`ERROR: ${this.addTimeStamp(sanitizedMessage, sanitizedObject)}`);
        }
    }
    // enable custom styles to help dev identify important info in the log
    infoStyle(...args: any): void {
        this.log("INFO", args[0]);
        if (this.enabled) {
            let color = args[1] ?? "";
            if (!color.includes(":")) {
                color = `color: ${color}`;
            }
            const style = color ?? "color: green";
            console.log(`%c${args[0]}`, style);
        }
    }

    addTimeStamp = (msg: string, object: any = undefined): string => {
        const serializedObject =
            object === undefined || object === null ? "" : typeof object === "string" ? object : JSON.stringify(object);

        return ` ${this.getTimeAndDelta()} ${msg ?? ""} ${serializedObject}`;
    };

    getLogs(): string[] {
        return this.logs;
    }

    private getTimeAndDelta(): string {
        if (!deltaLoggingEnabled) {
            return `[NPC] `;
        }

        const tm = new Date().getTime();

        const delta = tm - this.tmLast;
        this.tmLast = tm;

        return `[NPC ${this.getHMS()} diff ${String(delta).padStart(5, "0")} ms] `;
    }

    private getHMS(): string {
        const tm = new Date();
        return `${tm.toString().substring(16, 24)}.${String(tm.getMilliseconds()).padStart(3, "0")}`;
    }

    private getTimestamp(): string {
        return `[${new Date().toUTCString()}]`;
    }

    /**
     * Recursively masks the values of any keys that may contain sensitive credential data
     * (e.g. AWS security tokens, authorization signatures, secrets).
     * Sensitive strings embedded inside a plain string message are also redacted.
     */
    static sanitizeForLogging(value: any, _seen: WeakSet<object> = new WeakSet()): any {
        if (value === null || value === undefined) {
            return value;
        }

        if (typeof value === "string") {
            // Redact common credential patterns that may appear inline in message strings
            return value
                .replace(/(x-amz-security-token["']?\s*[=:,]\s*["']?)[^"',\s&}]+/gi, "$1[REDACTED]")
                .replace(
                    /(Authorization["']?\s*[=:,]\s*["']?(?:AWS4-HMAC-SHA256|Bearer)\s)[^"',\n}]+/gi,
                    "$1[REDACTED]"
                )
                .replace(/(X-Amz-Security-Token=)[^&"'\s]+/gi, "$1[REDACTED]")
                .replace(/(X-Amz-Signature=)[^&"'\s]+/gi, "$1[REDACTED]");
        }

        if (typeof value !== "object") {
            return value;
        }

        if (_seen.has(value)) {
            return "[Circular]";
        }
        _seen.add(value);

        if (Array.isArray(value)) {
            return value.map((item) => Logger.sanitizeForLogging(item, _seen));
        }

        const SENSITIVE_KEY_PATTERN = /authorization|x-amz-security-token|x-amz-signature|secretaccesskey|sessiontoken|idtoken|accesstoken|password|secret/i;
        const sanitized: any = {};
        for (const key of Object.keys(value)) {
            if (SENSITIVE_KEY_PATTERN.test(key)) {
                sanitized[key] = "[REDACTED]";
            } else {
                sanitized[key] = Logger.sanitizeForLogging(value[key], _seen);
            }
        }
        return sanitized;
    }

    private log(prefix: string, message: string, object?: any): void {
        const sanitizedMessage = Logger.sanitizeForLogging(message);

        let serializedObject = "";
        if (object !== undefined && object !== null) {
            // Normalize Error objects (non-enumerable properties) before sanitizing
            const normalized =
                object instanceof Error
                    ? JSON.parse(JSON.stringify(object, Object.getOwnPropertyNames(object)))
                    : object;
            const sanitized = Logger.sanitizeForLogging(normalized);
            serializedObject = JSON.stringify(sanitized);
        }

        const completeMessage = `${this.getTimestamp()} ${prefix}: ${sanitizedMessage} ${serializedObject}`;

        if (this.logs.length >= this.maximumLogLength) {
            this.logs.shift();
        }
        this.logs.push(completeMessage);
    }
}

let Log: Logger = new Logger();

export { Log };
